export const monthNames = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

export type commentsProps = {
  action: string;
  comment: string;
  createdBy: string;
  createdOn: string;
  id: number;
  jobId: number;
  roleTypeId: number;
  fileAttachmentId: number;
  fileAttachmentName: string;
  fileAttachmentTypeName: string;
};

export type CompanyProps = {
  companyId: number;
  companyName: string;
  regionIds: string;
};

export type RegionProps = {
  regionId: number | -1; // Allow -1 as a special case identifier for 'All'
  regionName: string;
};

export type CommunitiesProps = {
  company: string;
  companyId: number;
  jobCount: number;
  month: number;
  projectId: number;
  projectNumber: number;
  regionId: number;
  regionName: string;
  subDivision: string;
  year: number;
};

export type JobProps = {
  isResubmittedJob: boolean;
  reviewStatus: string;
  jobId: number;
  year: string;
  month: number;
  companyId: string;
  projectId: number;
  areaId: number;
  regionId: number;
  areaName: string;
  jobAddress: string;
  subdivision: string;
  jobNumber: string;
  projectNumber: string;
  stageCode: string;
  stageDate: string; // ISO date string from API
  buyer: string;
  buyerCombined: string;
  planNumber: string;
  planDescription: string;
  planElevationNumber: string;
  jobTypeCode: string;
  profitAnalysisType: string;
  startDate: string; // ISO date string from API
  soldDate: string; // ISO date string from API
  salesPrice: number;
  projectedCloseDate: string; // ISO date string from API
  hierLevel1Number: string;
  hierLevel1: string;
  hierLevel2: string;
  hierLevel3: string;
  hierLevel4: string;
  hierLevel5: string;
  hierLevel6: string;
  hierLevel7: string;
  hierLevel8: string;
  listPrice: number;
  builder: string;
};

export type fileAttachmentsTypesProps = {
  name: string;
  fileAttachmentTypesId: number;
  isRequired: boolean;
};

export type fileAttachmentsByTypesProps = {
  fileAttachmentName: string;
  fileAttachmentTypeId: number;
  fileAttachments: fileAttachmentsProps[];
  isRequired: boolean;
};

export type fileAttachmentsProps = {
  attachedFile: string;
  createdBy: string;
  createdOn: string;
  fileAttachmentId: number;
  fileAttachmentTypeId: number;
  fileName: string;
  fileUri: string;
  isActive: boolean;
  jobId: number;
  updatedBy: string;
  updatedOn: string;
  fileBlob?: any;
  isMergedFile: boolean;
};

export type JobDetailsProps = {
  areaName: string;
  areadId: number;
  buyer: string;
  buyerCombined: string;
  builders: BuilderInfo[];
  companyId: string;
  jobAddress: string;
  jobId: number;
  jobNumber: string;
  jobTypeCode: string;
  listPrice: number;
  month: number;
  planDescription: string;
  planElevationNumber: string;
  planNumber: string;
  profitAnalysisType: string;
  projectId: number;
  projectNumber: string;
  projectedCloseDate: string;
  regionId: number;
  reviewStatus: string;
  salesPrice: number;
  soldDate: string;
  stageCode: string;
  stageDate: string;
  startDate: string;
  subdivision: string;
  year: string;
  stage5Date: string;
  closeDate: string;
  fileAttachmentTypes: fileAttachmentsTypesProps[];
  fileAttachments: fileAttachmentsProps[];
  fileAttachmentsByTypes: fileAttachmentsByTypesProps[];
  bonusDetail: BonusDetail;
  reviewcomments: commentsProps[];
  payrollProcessedOn: string;
  stage5BonusPrice: number;
  isPayrollCycleSkipped?: boolean;
};

export type BuilderInfo = {
  builderId: number;
  jobId: number;
  builderName: string;
  builderNumber: number;
  employeeNumber: number;
  builderEmail: string;
  split: number;
  createdOn: string;
  createdBy: string;
  updatedOn: string;
  updatedBy: string;
  isActive: boolean;
  splitAmount: number;
  deductionAmount: number;
  bonusTypeId: number;
  isAutoApprovalEligible: boolean;
};

export type BuilderHistoryInfo = BuilderInfo & {
  builderHistoryId: number;
  action: string;
};

export type BonusProps = {
  biM360UploadIssue: boolean;
  bonusId: number;
  comments: any[];
  createdBy: string;
  createdOn: string;
  jobId: number;
  opsReviewedOn: string;
  opsReviewer: string;
  pmReviewedOn: string;
  pmReviewer: string;
  spmReviewedOn: string;
  spmReviewer: string;
  dmReviewedOn: string;
  dmReviewer: string;
  statusTypeId: number;
  updatedBy: string;
  updatedOn: string;
  regionName: string;
  payrollPaidOn: string;
  payrollProcessedOn: string;
  subdivision: string;
  jobAddress: string;
  jobNumber: string;
  closeDate?: string;
  /** Present when listing Stage 5 bonuses for review */
  stage5Date?: string | null;
  builders?: BuilderInfo[];
  projectNumber: string;
  payoutInfo?: {
    projectNumber?: string;
  };
  isResubmittedJob?: boolean;
  isPayrollCycleSkipped?: boolean;
  id: number;
  builderName: string;
  isApproved?: boolean | null;            // true=approved, false=rejected, null=undecided
  rejectionReason?: string;
  stage5Bonus?: number;
  stage5Price?: number;
  isAutoApprovalEligible?: boolean;
};

export type ProjectsProps = {
  companyId: number;
  companyName: string;
  projectId: number;
  projectNumber: number;
  subdivision: string;
  areaId: number;
  areaName: string;
  regionId: number;
  isPmSkipped: boolean;
  isSpmSkipped: boolean;
  skipReviews: {
    skipReviewId: number;
    projectId: number;
    reviewStepId: number;
  }[];
};

export interface BonusAmounts {
  bonusAmountsId: number;
  projectAndJobNumber: string;
  jobId: number;
  stage5Bonus?: number | null;
  stage5BonusPrice?: number | null;
  stage5Date?: string | null;
  stage5Builders?: string | null;
  closingBonus?: number | null;
  closingBonusPrice?: number | null;
  createdOn: string;
  createdBy: string;
  updatedOn?: string | null;
  updatedBy?: string | null;
  closingDate?: string | null;
  stage5BonusPaymentDate?: string | null;
  closingBonusPaymentDate?: string | null;
  isStage5BonusLocked?: boolean | null;
  isClosingBonusLocked?: boolean | null;
  stage5SalesPrice?: number | null;
  stage5ListPrice?: number | null;
  stage5JobRunDate?: string | null;
  closingSalesPrice?: number | null;
  closingListPrice?: number | null;
  closingJobRunDate?: string | null;
  comments?: string | null;
}

export interface BonusAmountsHistory {
  bonusAmountsHistoryId: number;
  bonusAmountsId: number;
  projectAndJobNumber: string;
  jobId: number;
  stage5Bonus?: number | null;
  stage5BonusPrice?: number | null;
  stage5Date?: string | null;
  stage5Builders?: string | null;
  stage5BonusPaymentDate?: string | null;
  closingBonus?: number | null;
  closingBonusPrice?: number | null;
  closingBonusPaymentDate?: string | null;
  createdOn: string;
  createdBy: string;
  updatedOn?: string | null;
  updatedBy?: string | null;
  closingDate?: string | null;
  isStage5BonusLocked?: boolean | null;
  isClosingBonusLocked?: boolean | null;
  stage5SalesPrice?: number | null;
  stage5ListPrice?: number | null;
  stage5JobRunDate?: string | null;
  closingSalesPrice?: number | null;
  closingListPrice?: number | null;
  closingJobRunDate?: string | null;
  comments?: string | null;
}

export interface PayrollJobResult {
  amount: number | null;
  bonusTypeId: number;
  multiCorrectionCount: number | null;
  hasNegativeBonus: boolean | null;
  payDate: string | null; // ISO 8601 date string
}

export interface BonusDetail {
  isReadyOnly: boolean;
  builders : BuilderInfo[] | null;
  builderHistories: BuilderHistoryInfo[];

  bonusAmounts: BonusAmounts | null;
  bonusAmountsHistories: BonusAmountsHistory[];

  stage5Payroll : PayrollJobResult | null;
  closingPayroll : PayrollJobResult | null;
  isBustedAndResold: boolean;
  wasRejected: boolean;
  isModelHome: boolean;
}

export interface CommentProps {
  Id: number;
  JobId: number;
  RoleTypeId: number;
  Action: string;
  Comment: string;
  CreatedBy: string;
  FileAttachmentId?: number;
  BonusTypeId: number;
  Name: string;
}


// RoleType Usage:
// RoleType[8] returns 'OpsPlus'
// RoleType.OpsPlus returns 8
// RoleTypeDescriptions[8] returns the full description
export const RoleType: Record<number, string> = {
  0: 'None',
  1: 'Uploader',
  2: 'PM',
  3: 'SPM',
  4: 'DM',
  5: 'Ops',
  6: 'Payroll',
  7: 'Admin',
  8: 'OpsPlus',
  100: 'System'
};

export const RoleTypeValue: Record<string, number> = {
  None: 0,
  Uploader: 1,
  PM: 2,
  SPM: 3,
  DM: 4,
  Ops: 5,
  Payroll: 6,
  Admin: 7,
  OpsPlus: 8,
  System: 100
};

export const RoleTypeDescriptions: Record<number, string> = {
  0: 'Invalid Role',
  1: 'Construction Manager II Or Construction Manager III',
  2: 'Project Manager',
  3: 'Senior Project Manager',
  4: 'Division Manager',
  5: 'Operations Coordinator',
  6: 'Payroll Manager, or Payroll Specialist',
  7: 'CBA Administrator',
  8: 'Director of Operations, Operations Manager or Assistant Operations Manager',
  100: 'System triggered updates (eg: when StatusType changes to Busted, New or InPMReview)'
};
 
export interface BonusStatusSummaryApi {
  id: number;
  jobId: number;
  jobNumber: string;
  projectNumber: string;
  bonusTypeId: number;
  bonusTypeName: string;
  currentStatus: string;
  lastUpdated: string;
  lastUpdatedBy: string;
  pmReviewedOn?: string | null;
  pmReviewer?: string | null;
  opsReviewedOn?: string | null;
  opsReviewer?: string | null;
  spmReviewedOn?: string | null;
  spmReviewer?: string | null;
  dmReviewedOn?: string | null;
  dmReviewer?: string | null;
  payrollProcessedOn?: string | null;
  payrollProcessor?: string | null;
  payrollPaidOn?: string | null;
  payrollPaidBy?: string | null;
  bim360Upload: boolean;
  bim360UploadOn?: string | null;
  bim360UploadIssue?: string | null;
  createdOn: string;
  createdBy: string;
}

export interface BonusStatusTimelineEventApi {
  eventTimestamp: string;
  eventType: string;
  bonusTypeId: number;
  bonusTypeName: string;
  updatedByUser: string;
  roleName: string;
  previousStatus?: string | null;
  currentStatus?: string | null;
  commentText?: string | null;
  action?: string | null;
  statusHistoryId?: number | null;
  commentId?: number | null;
  fileAttachmentId?: number | null;
  fileName?: string | null;
  attachedFile?: string | null;
}

// Transformed types (for use in components with Date objects)
export interface BonusStatusSummary {
  id: number;
  jobId: number;
  jobNumber: string;
  projectNumber: string;
  bonusTypeId: number;
  bonusTypeName: string;
  currentStatus: string;
  lastUpdated: Date;
  lastUpdatedBy: string;
  pmReviewedOn?: Date;
  pmReviewer?: string;
  opsReviewedOn?: Date;
  opsReviewer?: string;
  spmReviewedOn?: Date;
  spmReviewer?: string;
  dmReviewedOn?: Date;
  dmReviewer?: string;
  payrollProcessedOn?: Date;
  payrollProcessor?: string;
  payrollPaidOn?: Date;
  payrollPaidBy?: string;
  bim360Upload: boolean;
  bim360UploadOn?: Date;
  bim360UploadIssue?: string;
  createdOn: Date;
  createdBy: string;
}

export interface BonusStatusTimelineEvent {
  eventTimestamp: Date;
  eventType: string;
  bonusTypeId: number;
  bonusTypeName: string;
  updatedByUser: string;
  roleName: string;
  previousStatus?: string;
  currentStatus?: string;
  commentText?: string;
  action?: string;
  statusHistoryId?: number;
  commentId?: number;
  fileAttachmentId?: number;
  fileName?: string;
  attachedFile?: string;
}

export interface JobDetailBonusStatusTimelineResult {
  jobId: number;
  bonusStatusSummaries: BonusStatusSummary[];
  bonusStatusTimelineEvents: BonusStatusTimelineEvent[];
}

export enum BonusStatusTimelineEventType {
  StatusChange = 'StatusChange',
  Comment = 'Comment'
}

export enum BonusType {
  Stage5 = 1,
  Closing = 2
}

export interface BonusStatusTimelineFilters {
  bonusTypeId?: number;
  roleTypeId?: number;
  dateFrom?: Date;
  dateTo?: Date;
  eventType?: BonusStatusTimelineEventType;
}

export interface zBuilderActivityRecord {
  recordType: 'CurrentBuilder' | 'HistoryActivity';
  builderId?: number;
  builderHistoryId?: number;
  builderName: string;
  builderNumber?: number;
  builderEmail: string;
  employeeNumber?: number;
  split?: number;
  splitAmount?: number;
  deductionAmount?: number;
  bonusTypeName: string;
  action?: string;
  activityDescription: string;
  totalBonus?: number;
  activityBy?: string;
  activityDate?: string;
  commentId?: number;
  comment?: string;
  fileAttachmentId?: number;
  timelineSequence: number;
}

export interface JobDetailBuilderTimelineResult {
  jobId: number;
  stage5Activities: BuilderActivityRecord[];
  closingActivities: BuilderActivityRecord[];
}

export interface GetBuilderTimelineRequest {
  jobId: number;
  userRoleTypeId: number;
  authRoles: string[];
}
 
export interface BuilderActivityRecord {
  recordType: string;  // "CurrentBuilder" or "HistoryActivity"
  builderId: number | null;
  builderHistoryId: number | null;
  builderName: string;
  builderNumber: number | null;
  builderEmail: string;
  employeeNumber: number | null;
  split: number | null;
  splitAmount: number | null;
  deductionAmount: number | null;
  bonusTypeName: string;
  action: string | null;
  activityDescription: string;
  totalBonus: number | null;
  activityBy: string | null;
  activityDate: string | null;
  commentId: number | null;
  comment: string | null;
  fileAttachmentId: number | null;
  timelineSequence: number;
}

export interface JobDetailBuilderTimelineResult {
  jobId: number;
  stage5Activities: BuilderActivityRecord[];
  closingActivities: BuilderActivityRecord[];
}

export interface GetBuilderTimelineRequest {
  jobId: number;
  userRoleTypeId: number;
  authRoles: string[];
}

export interface AckDialogConfig {
  title: string;
  message: string;
};